import { useEffect } from "react";
import { Badge } from "@capawesome/capacitor-badge";
import { Capacitor } from "@capacitor/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/apiClient";
import { useSession } from "@/features/auth/hooks/useSession";

export const UNREAD_COUNT_KEY = ["notifications", "unread-count"];

/**
 * Hook to manage notification badge counter
 * - Fetches unread count from API (backup polling)
 * - Updates native app icon badge
 * - Event-driven updates via query invalidation
 */
export const useNotificationBadge = () => {
  const queryClient = useQueryClient();
  const { data: sessionData } = useSession();
  const isAuthenticated = sessionData?.isAuthenticated || false;

  // Fetch unread count (infrequent polling as backup)
  const { data: unreadCount = 0 } = useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get("/notifications/unread-count");
      return data.count;
    },
    enabled: !!isAuthenticated, // Only fetch if authenticated
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
    refetchInterval: 5 * 60 * 1000, // Backup sync every 5 minutes
    retry: false, // Do not retry on failure (especially 401)
  });

  // Update native badge when count changes
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    Badge.set({ count: unreadCount }).catch((err) =>
      console.error("Failed to set badge:", err),
    );
  }, [unreadCount]);

  const clearBadge = async () => {
    if (Capacitor.isNativePlatform()) {
      await Badge.clear();
    }
    queryClient.setQueryData(UNREAD_COUNT_KEY, 0);
  };

  const incrementBadge = () => {
    queryClient.setQueryData(UNREAD_COUNT_KEY, (old: number = 0) => old + 1);
  };

  const decrementBadge = () => {
    queryClient.setQueryData(UNREAD_COUNT_KEY, (old: number = 0) =>
      Math.max(0, old - 1),
    );
  };

  return { unreadCount, clearBadge, incrementBadge, decrementBadge };
};
